import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { useTelegram } from '../../../core/hooks/useTelegram';
import { tgWebAppSignInThunk, sendPhoneOtpThunk, verifyPhoneOtpThunk } from '../../../core/store/actions/authActions';
import { PrivateRoutes } from '../../../core/utils/constants';
import Button from '../../../components/UI/Button/Button';

import './Login.css';

const PLATFORMS = {
    TG: 'tg',
    MAX: 'max',
};

const RESEND_COOLDOWN_SECONDS = 30;

function ScreenLogin() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { user, error } = useSelector((state) => state.auth);
    const { auth_data: tg_auth_data } = useTelegram();

    useEffect(() => {
        if (user) {
            navigate(PrivateRoutes.Reservation);
        } else if (tg_auth_data) {
            dispatch(tgWebAppSignInThunk({ auth_data: tg_auth_data }));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ navigate, user ]);

    const [step, setStep] = useState('platform');
    const [platform, setPlatform] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otp, setOtp] = useState('');
    const [cooldown, setCooldown] = useState(0);
    const [pending, setPending] = useState(false);

    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
        return () => clearInterval(timer);
    }, [cooldown]);

    const pickPlatform = useCallback((value) => {
        setPlatform(value);
        setStep('phone');
    }, []);

    const requestCode = useCallback(async () => {
        if (phoneNumber.length !== 10 || cooldown > 0 || pending) return;

        setPending(true);
        const result = await dispatch(sendPhoneOtpThunk({ phone_number: phoneNumber, platform }));
        setPending(false);

        if (sendPhoneOtpThunk.fulfilled.match(result)) {
            setCooldown(RESEND_COOLDOWN_SECONDS);
            setStep('code');
        }
    }, [dispatch, phoneNumber, platform, cooldown, pending]);

    const confirmCode = useCallback(async () => {
        if (otp.length === 0 || pending) return;

        setPending(true);
        await dispatch(verifyPhoneOtpThunk({ phone_number: phoneNumber, otp }));
        setPending(false);
    }, [dispatch, phoneNumber, otp, pending]);

    if (tg_auth_data) {
        return null;
    }

    return (
        <div className='login-container'>
            {step === 'platform' && (
                <div className='login-platform-picker'>
                    <span className='login-panel-span'>Куда прислать код для входа?</span>
                    <Button text='Telegram' color='black' onClick={() => pickPlatform(PLATFORMS.TG)} />
                    <Button text='MAX' color='black' onClick={() => pickPlatform(PLATFORMS.MAX)} />
                </div>
            )}

            {step === 'phone' && (
                <div className='login-phone-form'>
                    <span className='login-panel-span'>Номер телефона (10 цифр, без кода страны)</span>
                    <input
                        type='tel'
                        inputMode='numeric'
                        className='login-input'
                        value={phoneNumber}
                        maxLength={10}
                        onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        placeholder='9991234567'
                        autoFocus
                    />
                    <Button text='Получить код' onClick={requestCode} />
                    <button type='button' className='login-link-button' onClick={() => setStep('platform')}>
                        Назад
                    </button>
                </div>
            )}

            {step === 'code' && (
                <div className='login-phone-form'>
                    <span className='login-panel-span'>Код отправлен в {platform === PLATFORMS.TG ? 'Telegram' : 'MAX'}</span>
                    <input
                        type='text'
                        inputMode='numeric'
                        className='login-input'
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                        placeholder='Код из сообщения'
                        autoFocus
                    />
                    <Button text='Войти' onClick={confirmCode} />
                    <button
                        type='button'
                        className='login-link-button'
                        disabled={cooldown > 0 || pending}
                        onClick={requestCode}
                    >
                        {cooldown > 0 ? `Отправить код ещё раз (${cooldown}с)` : 'Отправить код ещё раз'}
                    </button>
                </div>
            )}

            {error && <div className='login-error'>{error}</div>}
        </div>
    );
}


export default ScreenLogin;
